import {
  differenceInCalendarMonths,
  format,
  isSameMonth,
  startOfMonth,
} from 'date-fns';

export type CalendarPeriod = 'month' | 'year';

export type CalendarPeriodBounds = {
  fromMonth?: Date;
  toMonth?: Date;
};

export type CalendarPeriodOption = {
  label: string;
  month: Date;
  disabled: boolean;
  selected: boolean;
  current: boolean;
};

const FIRST_YEAR = 1900;
const LAST_YEAR = 2100;

const isBeforeFromMonth = (month: Date, { fromMonth }: CalendarPeriodBounds) =>
  fromMonth !== undefined && differenceInCalendarMonths(month, fromMonth) < 0;

const isAfterToMonth = (month: Date, { toMonth }: CalendarPeriodBounds) =>
  toMonth !== undefined && differenceInCalendarMonths(month, toMonth) > 0;

export const isWithinBounds = (month: Date, bounds: CalendarPeriodBounds) =>
  !isBeforeFromMonth(month, bounds) && !isAfterToMonth(month, bounds);

export const clampMonth = (month: Date, bounds: CalendarPeriodBounds) => {
  if (bounds.fromMonth && isBeforeFromMonth(month, bounds)) {
    return startOfMonth(bounds.fromMonth);
  }
  if (bounds.toMonth && isAfterToMonth(month, bounds)) {
    return startOfMonth(bounds.toMonth);
  }
  return startOfMonth(month);
};

export const getMonthOptions = (
  anchor: Date,
  bounds: CalendarPeriodBounds,
  today: Date
): CalendarPeriodOption[] =>
  Array.from({ length: 12 }, (_, monthIndex) => {
    const month = new Date(anchor.getFullYear(), monthIndex, 1);
    return {
      label: format(month, 'MMM'),
      month,
      disabled: !isWithinBounds(month, bounds),
      selected: monthIndex === anchor.getMonth(),
      current: isSameMonth(month, today),
    };
  });

export const getYearOptions = (
  anchor: Date,
  bounds: CalendarPeriodBounds,
  today: Date
): CalendarPeriodOption[] => {
  const anchorYear = anchor.getFullYear();
  const firstYear =
    bounds.fromMonth?.getFullYear() ?? Math.min(FIRST_YEAR, anchorYear);
  const lastYear =
    bounds.toMonth?.getFullYear() ?? Math.max(LAST_YEAR, anchorYear);

  return Array.from(
    { length: Math.max(0, lastYear - firstYear + 1) },
    (_, index) => {
      const year = firstYear + index;
      return {
        label: String(year),
        month: clampMonth(new Date(year, anchor.getMonth(), 1), bounds),
        disabled: false,
        selected: year === anchorYear,
        current: year === today.getFullYear(),
      };
    }
  );
};

export const getInitialFocusIndex = (options: CalendarPeriodOption[]) => {
  const selectedIndex = options.findIndex(
    (option) => option.selected && !option.disabled
  );
  const index =
    selectedIndex === -1
      ? options.findIndex((option) => !option.disabled)
      : selectedIndex;
  return index === -1 ? undefined : index;
};

export const getArrowKeyStep = (key: string, columns: number) => {
  switch (key) {
    case 'ArrowLeft':
      return -1;
    case 'ArrowRight':
      return 1;
    case 'ArrowUp':
      return -columns;
    case 'ArrowDown':
      return columns;
    default:
      return undefined;
  }
};

export const findNextEnabledIndex = (
  options: CalendarPeriodOption[],
  fromIndex: number,
  step: number
) => {
  for (
    let index = fromIndex + step;
    index >= 0 && index < options.length;
    index += step
  ) {
    if (options[index]?.disabled === false) {
      return index;
    }
  }
  return undefined;
};
